/**
 * Shop-by-brand section enhancement.
 *
 * Custom element <shop-by-brand> attaches:
 *   - Client-side search filter on the SSR-rendered list of brand cards
 *   - Match highlighting via <mark>
 *   - Fade-in stagger via IntersectionObserver (skipped under reduced-motion)
 *   - Auto-hides letter dividers and disables alphabet-nav letters whose
 *     groups have no visible items after filtering
 *   - Empty-state toggle with aria-live announcement
 *
 * No fetch, no body-level mutation, no inline handlers. The HTML is fully
 * functional without this script — JS is purely progressive enhancement.
 *
 * Performance: ~2.5 KB minified, well under Shopify's 16 KB-per-bundle budget.
 */

(function () {
  'use strict';

  const theme = window.theme || {};

  // Strip HTML so we never feed user input back as markup. Required, not
  // optional: the highlighter re-renders brand names through innerHTML, and an
  // escaper that silently degraded to identity would make that an injection
  // vector. utils.js is a <head> foundation script, so its absence means the
  // page is broken anyway.
  if (!theme.utils || !theme.utils.escapeHtml) return;
  if (customElements.get('shop-by-brand')) return;

  const escapeHtml = theme.utils.escapeHtml;

  const HIGHLIGHT_REGEX_ESCAPE = /[.*+?^${}()|[\]\\]/g;
  const escapeRegex = (s) => s.replace(HIGHLIGHT_REGEX_ESCAPE, '\\$&');

  // Accent-insensitive comparison: NFD decomposes accented chars into base + combining
  // mark, then we strip the combining marks (Unicode block U+0300..U+036F).
  // "Café" → "cafe", "Žižek" → "zizek", "Müller" → "muller".
  // String.prototype.normalize is Baseline since 2016 (all evergreen browsers).
  const stripDiacritics = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

  class ShopByBrand extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-shop-by-brand-search]');
      this.body = this.querySelector('[data-shop-by-brand-body]');
      this.empty = this.querySelector('[data-shop-by-brand-empty]');
      this.items = Array.from(this.querySelectorAll('.shop-by-brand__item'));
      this.dividers = Array.from(this.querySelectorAll('[data-shop-by-brand-letter]'));
      this.alphabetLinks = Array.from(this.querySelectorAll('[data-shop-by-brand-letter-link]'));

      // Cache the original title text plus a normalized (lowercased,
      // diacritic-stripped) form for fast accent-insensitive matching.
      // Two forms are kept so we can match against `searchable` while still
      // running the highlight regex against the raw `original` text.
      this.itemNames = this.items.map((li) => {
        const name = li.querySelector('.shop-by-brand__name');
        const raw = (li.dataset.vendor || '').trim();
        return {
          el: li,
          nameEl: name,
          original: name ? name.textContent : '',
          searchable: stripDiacritics(raw),
        };
      });

      if (this.input) {
        // Progressive-enhancement hydration: input was rendered with the
        // `disabled` attribute so it's unusable when JS fails or hasn't
        // loaded yet. Now that we're hydrated, enable it.
        this.input.removeAttribute('disabled');
        this.onInput = this.onInput.bind(this);
        this.input.addEventListener('input', this.onInput);
      }

      this.setupFadeIn();
    }

    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('input', this.onInput);
      if (this.observer) this.observer.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    onInput() {
      // Debounce via rAF — lighter than setTimeout, naturally coalesces with
      // paint frames so the input stays responsive even on long lists.
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.applyFilter());
    }

    applyFilter() {
      // Visibility match uses normalized (diacritic-stripped) forms so users
      // typing "cafe" find vendors named "Café". Highlight regex is built
      // from the raw query and applied to the raw original text — when query
      // and vendor differ only in diacritics, the visual highlight may not
      // hit, but the card is still surfaced (which is the higher-value win).
      const rawQuery = this.input.value.trim();
      const normalizedQuery = stripDiacritics(rawQuery.toLowerCase());
      const hasQuery = normalizedQuery.length > 0;
      const matchesPerLetter = Object.create(null);
      let totalVisible = 0;

      const highlightRegex = hasQuery && rawQuery.length > 0 ? new RegExp(escapeRegex(rawQuery), 'gi') : null;

      for (const entry of this.itemNames) {
        const isMatch = !hasQuery || entry.searchable.toLowerCase().includes(normalizedQuery);
        entry.el.hidden = !isMatch;

        if (isMatch) {
          totalVisible += 1;
          // Track which letter buckets still have visible items.
          const letter = entry.searchable.charAt(0).toUpperCase();
          const bucket = /[A-Z]/.test(letter) ? letter : '#';
          matchesPerLetter[bucket] = (matchesPerLetter[bucket] || 0) + 1;
        }

        // Restore or apply the highlight in O(1) per item.
        if (entry.nameEl) {
          if (hasQuery && isMatch && highlightRegex) {
            entry.nameEl.innerHTML = escapeHtml(entry.original).replace(highlightRegex, (m) => '<mark>' + escapeHtml(m) + '</mark>');
          } else {
            entry.nameEl.textContent = entry.original;
          }
        }
      }

      // Toggle letter dividers based on whether their group has matches.
      for (const divider of this.dividers) {
        const letter = divider.dataset.shopByBrandLetter;
        if (matchesPerLetter[letter]) {
          divider.removeAttribute('data-hidden');
        } else {
          divider.setAttribute('data-hidden', '');
        }
      }

      // Disable alphabet-nav letters with no matches (visual cue + click
      // suppression). Empty buckets are kept in the DOM so the layout
      // doesn't shift on every keystroke.
      for (const link of this.alphabetLinks) {
        const letter = link.dataset.shopByBrandLetterLink;
        link.classList.toggle('is-empty', !matchesPerLetter[letter]);
      }

      if (this.empty) this.empty.hidden = totalVisible > 0;
    }

    setupFadeIn() {
      // Bail out for users who prefer reduced motion. The CSS already has
      // a `prefers-reduced-motion: no-preference` guard, but skipping the
      // observer avoids unnecessary work.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        for (const item of this.items) item.classList.add('is-visible');
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        for (const item of this.items) item.classList.add('is-visible');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
      );

      for (const item of this.items) this.observer.observe(item);
    }
  }

  customElements.define('shop-by-brand', ShopByBrand);
})();
